import React, { useRef, useState } from "react";
import Draggable from "react-draggable";
import { getSnapAdjustment } from "../../../utils/alignmentGuides";

const ZERO_POSITION = { x: 0, y: 0 };
const NO_GUIDES = { verticalGuide: null, horizontalGuide: null, verticalGuideKind: null, horizontalGuideKind: null };
const DRAG_THRESHOLD = 3;

const DraggableElement = ({
  bounds,
  disabled,
  children,
  canvasRef,
  setResizeDimensions,
  resizableDimensions,
  element,
  siblingDimensions,
  groupBounds,
  pageDimensions,
  onGuidesChange,
  isSelected,
  isResizing,
  onSelect,
  onGroupDragStart,
  onGroupDrag,
  onGroupDragStop,
  onDragStop,
  onDragOffset,
  onEditClick
}) => {
  const wasDraggedRef = useRef(false);
  const [offset, setOffset] = useState(ZERO_POSITION);
  const offsetRef = useRef(offset);
  const rawOffsetRef = useRef(ZERO_POSITION);

  const [isDragging, setIsDragging] = useState(false);

  const groupPageBounds =
    groupBounds && pageDimensions
      ? {
          left: -groupBounds.left,
          top: -groupBounds.top,
          right: pageDimensions.width - (groupBounds.left + groupBounds.width),
          bottom: pageDimensions.height - (groupBounds.top + groupBounds.height),
        }
      : null;

  const handleDrag = (e, data) => {
    const raw = {
      x: rawOffsetRef.current.x + data.deltaX,
      y: rawOffsetRef.current.y + data.deltaY,
    };

    rawOffsetRef.current = raw;

    if (Math.abs(raw.x) > DRAG_THRESHOLD || Math.abs(raw.y) > DRAG_THRESHOLD) {
      if (!wasDraggedRef.current) setIsDragging(true);
      wasDraggedRef.current = true;
    }

    let next = raw;

    if (onGuidesChange) {
      const movingBox = groupBounds
        ? {
            top: groupBounds.top + raw.y,
            left: groupBounds.left + raw.x,
            width: groupBounds.width,
            height: groupBounds.height,
          }
        : {
            top: resizableDimensions.top + raw.y,
            left: resizableDimensions.left + raw.x,
            width: resizableDimensions.width,
            height: resizableDimensions.height,
          };
      const { deltaX, deltaY, verticalGuide, horizontalGuide, verticalGuideKind, horizontalGuideKind } = getSnapAdjustment(
        movingBox,
        siblingDimensions,
        pageDimensions
      );
      next = { x: raw.x + deltaX, y: raw.y + deltaY };
      onGuidesChange({ verticalGuide, horizontalGuide, verticalGuideKind, horizontalGuideKind });
    }

    if (groupBounds && pageDimensions) {
      const minX = -groupBounds.left;
      const maxX = pageDimensions.width - (groupBounds.left + groupBounds.width);
      const minY = -groupBounds.top;
      const maxY = pageDimensions.height - (groupBounds.top + groupBounds.height);
      next = {
        x: Math.min(Math.max(next.x, minX), maxX),
        y: Math.min(Math.max(next.y, minY), maxY),
      };
    }

    offsetRef.current = next;
    setOffset(next);
    onGroupDrag?.(next);
    onDragOffset?.(next);
  };

  const handleDragStop = () => {
    document.body.classList.remove("element-dragging");
    onGuidesChange?.(NO_GUIDES);
    const { x, y } = offsetRef.current;
    offsetRef.current = ZERO_POSITION;
    rawOffsetRef.current = ZERO_POSITION;
    setOffset(ZERO_POSITION);
    setIsDragging(false);
    onDragOffset?.(ZERO_POSITION);

    if (!wasDraggedRef.current || !onGroupDragStop) {
      onSelect?.();
    }

    if (!wasDraggedRef.current) {
      return;
    }

    if (onGroupDragStop) {
      onGroupDragStop({ x, y });
      return;
    }

    if (!canvasRef) return;

    const finalPosition = {
      top: resizableDimensions.top + y,
      left: resizableDimensions.left + x,
    };

    setResizeDimensions(prevDimensions => {
      const dimensions = prevDimensions[element.id];
      const baseTop = dimensions?.top ?? resizableDimensions.top;
      const baseLeft = dimensions?.left ?? resizableDimensions.left;
      return {
        ...prevDimensions,
        [element.id]: {
          ...dimensions,
          top: baseTop + y,
          left: baseLeft + x
        }
      }
    });

    onDragStop?.(finalPosition);
  };

  return (
    <Draggable
      position={offset}
      onStart={() => {
        document.body.classList.add("element-dragging");
        wasDraggedRef.current = false;
        rawOffsetRef.current = ZERO_POSITION;
        onGroupDragStart?.();
      }}
      onDrag={handleDrag}
      onStop={handleDragStop}
      bounds={groupPageBounds ?? (element?.geometry?.keep_within_page ? bounds : false)}
      disabled={disabled}
    >
      <div
        className={isSelected && !isDragging && !isResizing ? "elem-selected" : undefined}
        onClickCapture={(e) => {
          if (wasDraggedRef.current) {
            e.stopPropagation();
            wasDraggedRef.current = false;
          }
        }}
        onDoubleClick={() => onEditClick?.()}
      >
        {children}
      </div>
    </Draggable>
  );
};

export default DraggableElement;
